import { Link } from "react-router-dom";
import { usePublicProjects } from "@/hooks/useProjects";
import { usePublicTestimonials } from "@/hooks/useTestimonials";
import { isConceptProject } from "@/lib/caseStudy";
import { Photo } from "@/components/shared/Photo";

function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export interface CaseRow {
  id: string;
  title: string;
  description: string | null;
  featured: boolean;
  display_order: number;
  case_study?: { metric?: string | null; metricLabel?: string | null } | null;
}

export interface CaseTestimonial {
  quote: string;
  author_name: string;
  author_role?: string | null;
}

export interface CasesWithTestimonialViewProps {
  /** Já ordenados e cortados em 3 pelo container. */
  cases: CaseRow[];
  /** Sem depoimento a grade vira uma coluna. */
  testimonial?: CaseTestimonial | null;
}

/**
 * Seção "Resultados de quem já trabalhou comigo" da home: até 3 cases à
 * esquerda e um depoimento em destaque à direita. Só apresentação — dados
 * vêm do container abaixo (site) ou de fixtures (Storybook).
 */
export function CasesWithTestimonialView({ cases, testimonial }: CasesWithTestimonialViewProps) {
  return (
    <section className="mx-auto max-w-[1180px] px-8 py-[76px] max-[900px]:px-5">
      <div
        className={`grid ${testimonial ? "grid-cols-2 max-[900px]:grid-cols-1" : "grid-cols-1"} items-center gap-11`}
      >
        <div>
          <h2 className="text-[31px] font-bold tracking-[-0.03em]">
            Resultados de quem já trabalhou comigo
          </h2>
          <p className="mb-[26px] mt-3.5 text-[15.5px] leading-[1.65] text-muted-foreground">
            Cases reais, com o número que mudou no negócio.
          </p>
          <div className="flex flex-col gap-3">
            {cases.map((p) => (
              <Link
                key={p.id}
                to={`/projetos/${p.id}`}
                className="flex items-center gap-4 rounded-[14px] border border-border bg-card px-[18px] py-4 transition-colors hover:bg-muted"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold">{p.title}</div>
                  <div className="mt-[3px] line-clamp-1 text-[12.5px] text-muted-foreground">
                    {p.description}
                  </div>
                </div>
                {p.case_study?.metric && (
                  <div className="shrink-0 text-right">
                    <div className="text-[17px] font-bold tracking-[-0.02em] text-primary">
                      {p.case_study.metric}
                    </div>
                    {p.case_study.metricLabel && (
                      <div className="text-[11px] text-muted-foreground">
                        {p.case_study.metricLabel}
                      </div>
                    )}
                  </div>
                )}
              </Link>
            ))}
            {cases.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Em breve novos cases serão adicionados.
              </p>
            ) : (
              <Link
                to="/projetos"
                className="mt-1.5 w-fit text-sm font-bold text-primary hover:text-primary-deep"
              >
                Ver todos os cases →
              </Link>
            )}
          </div>
        </div>

        {testimonial && (
          <div className="relative isolate overflow-hidden rounded-[20px] bg-deep px-[34px] py-[38px] text-deep-foreground">
            <div className="absolute inset-0 -z-10">
              <Photo id="dona-cafeteria" decorative sizes="(max-width: 900px) 100vw, 560px" />
              <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/90 to-deep/70" />
            </div>
            <div className="font-display text-[34px] leading-none text-primary">"</div>
            <p className="mt-2.5 text-lg font-medium leading-[1.6]">{testimonial.quote}</p>
            <div className="mt-[26px] flex items-center gap-[13px]">
              <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-deep-foreground/[0.12] text-[13px] font-bold">
                {initialsFor(testimonial.author_name)}
              </div>
              <div>
                <div className="text-[13.5px] font-bold">{testimonial.author_name}</div>
                {testimonial.author_role && (
                  <div className="text-xs text-deep-foreground/70">{testimonial.author_role}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** Container do site: busca projetos públicos e depoimentos aprovados. */
const CasesWithTestimonial = () => {
  const { data: projects } = usePublicProjects();
  const { data: testimonials } = usePublicTestimonials();

  // Projetos conceito ficam fora: a seção fala de resultado medido em cliente real.
  const cases = (projects || [])
    .filter((p) => !isConceptProject(p))
    .sort((a, b) => Number(b.featured) - Number(a.featured) || a.display_order - b.display_order)
    .slice(0, 3);

  return <CasesWithTestimonialView cases={cases} testimonial={testimonials?.[0]} />;
};

export default CasesWithTestimonial;
